"use client";
import { useState } from "react";
import { Minus, Plus } from "lucide-react";
import { faqData } from "@/constants";
import { FAQ } from "@/types";

const FAQItem = ({
  faq,
  isOpen,
  onClick,
}: {
  faq: FAQ;
  isOpen: boolean;
  onClick: () => void;
}) => {
  return (
    <div className="overflow-hidden rounded-xl border border-white/20 bg-black/25 md:border-[#2c2c2c] md:bg-[#131313]/60">
      <button
        onClick={onClick}
        className="flex w-full items-center justify-between px-5 py-[25px] text-white hover:no-underline md:h-auto md:min-h-[114px] md:px-10"
        aria-expanded={isOpen}
        aria-controls={`faq-content-${faq.question}`}
      >
        <span className="text-left text-sm leading-[17px] font-semibold md:text-base md:leading-normal md:font-normal">
          {faq.question}
        </span>
        <Plus
          className={`size-4 shrink-0 text-white transition-transform duration-200 ${
            isOpen ? "hidden" : ""
          }`}
        />
        <Minus
          className={`size-4 shrink-0 text-white transition-transform duration-200 ${
            isOpen ? "block" : "hidden"
          }`}
        />
      </button>
      <div
        id={`faq-content-${faq.question}`}
        className={`grid overflow-hidden transition-all duration-200 ${
          isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="overflow-hidden">
          <p className="px-5 pt-0 pb-5 text-sm text-[#dddddd] md:px-8 md:pb-6 md:text-base">
            {faq.answer}
          </p>
        </div>
      </div>
    </div>
  );
};

export function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const handleToggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="w-full bg-black pt-[78px] pb-[68px] md:py-[100px]">
      <div className="mx-auto flex flex-col items-center gap-[45px] px-3 md:gap-[60px]">
        <div className="max-w-2xl text-center">
          <h2 className="mb-[15px] bg-gradient-to-r from-[#ffffff] to-[#1E7BDB]/86 bg-clip-text text-2xl leading-[29px] font-semibold text-transparent md:text-4xl md:leading-tight md:font-normal lg:text-[45px]">
            Frequently Asked
            <br className="max-md:hidden" />
            Questions
          </h2>
          <p className="max-w-full text-sm leading-[17px] font-semibold text-[#dddddd] md:max-w-[588px] md:text-lg md:leading-snug md:font-medium">
            Find answers to common questions about our services and platform
          </p>
        </div>

        <div className="w-full max-w-[350px] space-y-2.5 md:max-w-4xl md:space-y-5">
          {faqData.map((faq, index) => (
            <FAQItem
              key={index}
              faq={faq}
              isOpen={openIndex === index}
              onClick={() => handleToggle(index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
